import {
    Component,
    input,
    output,
    signal,
    computed,
    ChangeDetectorRef,
    ElementRef,
    effect,
    model,
    ChangeDetectionStrategy,
} from "@angular/core";

import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { Workflow as WorkflowTypes } from "./service/Workflow";
import { WorkflowStatesComponent } from "./workflow.states.component";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import { ExpandTrigger, WFError, WorkflowSteps } from "./workflow.process.types";
import { S25Util } from "../../util/s25-util";

@TypeManagerDecorator("workflow-states-step")
@Component({
    selector: "workflow-states-step",
    template: `
        <div class="states-step-container">
            @if (error()?.message) {
                <div class="ngRed" [innerHTML]="error().message"></div>
            }

            <div class="step-header">
                <h3>Approvals</h3>
                <p class="step-description">
                    Configure approvals for each stage. Approvals represent parallel approval checkpoints.
                </p>
            </div>

            <div class="add-state-section">
                <div class="add-controls">
                    <select [(ngModel)]="selectedStageForNewState" class="stage-dropdown">
                        <option value="">Select stage...</option>
                        @for (stage of nonAccumulatorStages(); track stage.workflowStageId || stage.id) {
                            <option [value]="stage.workflowStageId || stage.id">
                                {{ stage.stageName }}
                            </option>
                        }
                    </select>
                    <button
                        class="btn btn-primary"
                        [disabled]="!selectedStageForNewState"
                        (click)="addStateToSelectedStage()"
                    >
                        Add Approval
                    </button>
                </div>
            </div>

            <div class="states-list">
                @for (stage of stagesWithStates(); track stage.workflowStageId || stage.id) {
                    <div class="state-item" [attr.data-state-id]="stage.workflowStageId || stage.id">
                        <div class="state-header" (click)="toggleStageExpansion(stage)">
                            <div class="state-info">
                                <span class="state-name">{{ stage.stageName }}</span>
                                <span class="approval-count">{{ stage.states!.length }} approval(s)</span>
                                <span class="approval-preview">{{ getApprovalNamesForStage(stage) }}</span>
                            </div>
                            <div class="state-actions">
                                <span class="expand-icon" [class.expanded]="isStageExpanded(stage)">
                                    {{ isStageExpanded(stage) ? "▼" : "▶" }}
                                </span>
                            </div>
                        </div>

                        @if (isStageExpanded(stage)) {
                            <div class="state-content">
                                <workflow-states
                                    [selectedStage]="stage"
                                    [workflowProcess]="workflowProcess()"
                                    [aiFeatureEnabled]="aiFeatureEnabled()"
                                    (statesChange)="onStageStatesChange(stage, $event)"
                                ></workflow-states>
                            </div>
                        }
                    </div>
                }
            </div>

            @if (getAllStates().length === 0) {
                <div class="no-states">
                    <p>
                        No approvals defined. Select a stage above and click "Add Approval" to create your first
                        approval.
                    </p>
                </div>
            }
        </div>
    `,
    styles: `
        .states-step-container {
            padding: 20px;
        }

        .step-header {
            margin-bottom: 24px;
        }

        .step-header h3 {
            margin: 0 0 8px 0;
            color: var(--color--text-strong);
        }

        .step-description {
            margin: 0;
            color: var(--color--text-muted);
            font-size: 14px;
        }

        .add-state-section {
            margin-bottom: 24px;
            padding: 16px;
            background: var(--color--surface-2-bg);
            border: 1px solid var(--color--border);
            border-radius: 8px;
        }

        .add-controls {
            display: flex;
            gap: 12px;
            align-items: center;
        }

        .stage-dropdown {
            flex: 1;
            padding: 8px 12px;
            border: 1px solid var(--color--border-bold);
            border-radius: 4px;
            background: var(--color--surface-bg);
        }

        .btn {
            padding: 8px 16px;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 500;
        }

        .btn-primary {
            background: var(--color--link-text);
            color: white;
        }

        .btn-primary:hover:not(:disabled) {
            background: var(--color--link-text-hover);
        }

        .btn-primary:disabled {
            background: var(--color--text-muted);
            cursor: not-allowed;
        }

        .states-list {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }

        .state-item {
            background: var(--color--surface-bg);
            border: 1px solid var(--color--border);
            border-radius: 8px;
            overflow: visible;
            position: relative;
            z-index: 10;
        }

        .state-header {
            padding: 16px;
            background: var(--color--surface-2-bg);
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            align-items: center;
            transition: background 0.2s;
        }

        .state-header:hover {
            background: var(--color--surface-2-bg-hover);
        }

        .state-info {
            display: flex;
            align-items: center;
            gap: 12px;
        }

        .state-name {
            font-weight: 500;
            color: var(--color--text-strong);
        }

        .stage-context {
            font-size: 12px;
            color: var(--color--text-muted);
            font-style: italic;
        }

        .state-flags {
            display: flex;
            gap: 8px;
        }

        .flag {
            padding: 2px 8px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 500;
            text-transform: uppercase;
        }

        .flag.enabled {
            background: var(--color--registration);
            color: white;
        }

        .expand-icon {
            color: var(--color--text-muted);
            font-size: 12px;
            transition: transform 0.2s;
        }

        .expand-icon.expanded {
            transform: rotate(90deg);
        }

        .state-content {
            padding: 20px;
            border-top: 1px solid var(--color--border);
            overflow: visible;
            position: relative;
            z-index: 1;
        }

        .approval-count {
            font-size: 12px;
            color: var(--color--text-muted);
            background: var(--color--surface-2-bg-hover);
            padding: 2px 8px;
            border-radius: 12px;
        }

        .approval-preview {
            font-size: 12px;
            color: var(--color--text-muted);
            font-style: italic;
        }

        .no-states {
            text-align: center;
            padding: 40px 20px;
            color: var(--color--text-muted);
        }

        .ngRed {
            font-size: 0.9rem;
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, WorkflowStatesComponent, S25DropdownPaginatedModule],
})
export class WorkflowStatesStepComponent {
    workflowProcess = input.required<WorkflowTypes.WorkflowProcess>();
    error = input<WFError>();
    aiFeatureEnabled = input(false);
    selectedStateTrigger = model<ExpandTrigger>();
    workflowProcessChange = output<WorkflowTypes.WorkflowProcess>();

    selectedStageForNewState: string = "";
    private expandedStages = signal<Set<number | string>>(new Set());

    constructor(
        private cd: ChangeDetectorRef,
        private el: ElementRef,
    ) {
        effect(() => {
            const expandTrigger = this.selectedStateTrigger();
            if (expandTrigger && expandTrigger.step === WorkflowSteps.States && S25Util.isDefined(expandTrigger.id)) {
                this.expandStateById(expandTrigger.id);
                this.selectedStateTrigger.set(undefined);
            }
        });
    }

    readonly nonAccumulatorStages = computed(
        () => this.workflowProcess().stages?.filter((stage) => !stage.isAccumulator && !stage.isApAccumulator) || [],
    );

    getAllStates(): WorkflowTypes.WorkflowState[] {
        const allStates: WorkflowTypes.WorkflowState[] = [];
        this.workflowProcess().stages?.forEach((stage) => {
            if (stage.states) {
                allStates.push(...stage.states);
            }
        });
        return allStates;
    }

    getStageForState(state: WorkflowTypes.WorkflowState): WorkflowTypes.WorkflowStage | null {
        return (
            this.workflowProcess().stages?.find((stage) =>
                stage.states?.some(
                    (s) =>
                        (s.workflowStateId && s.workflowStateId === state.workflowStateId) ||
                        (s.id && s.id === state.id),
                ),
            ) || null
        );
    }

    addStateToSelectedStage() {
        if (!this.selectedStageForNewState) return;

        const stage = this.workflowProcess().stages?.find(
            (s) =>
                (s.workflowStageId && s.workflowStageId.toString() === this.selectedStageForNewState) ||
                (s.id && s.id.toString() === this.selectedStageForNewState),
        );

        if (stage && (stage.isAccumulator || stage.isApAccumulator)) {
            // Accumulator stages cannot have states - would require merging child completion and approval completion mechanisms
            return;
        }

        if (stage) {
            const newState: WorkflowTypes.WorkflowState = {
                id: this.getNextNegativeId(),
                workflowProcessId: this.workflowProcess().workflowProcessId,
                stateName: `Approval ${(stage.states || []).length + 1}`,
                defaultEnabled: true,
                description: "",
                contacts: [],
            };

            if (!stage.states) {
                stage.states = [];
            }
            stage.states.push(newState);

            this.workflowProcessChange.emit(this.workflowProcess());
            this.cd.detectChanges();
        }
    }

    private getNextNegativeId(): number {
        const allStates = this.getAllStates();
        return allStates.reduce((minId, state) => Math.min(minId, state.id || -1), -1) - 1;
    }

    readonly stagesWithStates = computed(() => (this.workflowProcess().stages || []).filter((s) => s.states?.length));

    getApprovalNamesForStage(stage: WorkflowTypes.WorkflowStage): string {
        return stage.states?.map((s) => s.stateName).join(", ") ?? "";
    }

    toggleStageExpansion(stage: WorkflowTypes.WorkflowStage) {
        const stageKey = stage.workflowStageId || stage.id!;
        const expanded = this.expandedStages();

        if (expanded.has(stageKey)) {
            expanded.delete(stageKey);
        } else {
            expanded.add(stageKey);
        }

        this.expandedStages.set(new Set(expanded));
    }

    isStageExpanded(stage: WorkflowTypes.WorkflowStage): boolean {
        const stageKey = stage.workflowStageId || stage.id!;
        return this.expandedStages().has(stageKey);
    }

    onStageStatesChange(stage: WorkflowTypes.WorkflowStage, states: WorkflowTypes.WorkflowState[]) {
        if (stage) {
            stage.states = states;
        }
        this.workflowProcessChange.emit(this.workflowProcess());
        this.cd.detectChanges();
    }

    expandStateById(stateId: number) {
        const stage = this.workflowProcess().stages?.find((s) =>
            s.states?.some((st) => st.workflowStateId === stateId || st.id === stateId),
        );
        if (stage) {
            const stageKey = stage.workflowStageId || stage.id!;
            const expanded = this.expandedStages();
            if (!expanded.has(stageKey)) {
                expanded.add(stageKey);
                this.expandedStages.set(new Set(expanded));
            }
            setTimeout(() => {
                const stageEl = this.el.nativeElement.querySelector(`[data-state-id="${stageKey}"]`);
                stageEl?.scrollIntoView({ behavior: "smooth", block: "start" });
            });
        }
    }
}
